import { ChevronDown, Container, Clock } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import StatusBadge from './StatusBadge';
import type { ServiceDetail } from '@/lib/types';

interface ServiceRowProps {
  service: ServiceDetail;
}

export default function ServiceRow({ service }: ServiceRowProps) {
  const [expanded, setExpanded] = useState(false);

  const status = service.healthy ? 'healthy' : service.desired === 0 ? 'unknown' : 'critical';

  return (
    <div className="border-b border-white/[0.04] last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center gap-4 px-4 py-3 text-left transition-colors',
          'hover:bg-white/[0.02]',
        )}
      >
        {/* Service name */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Container className="w-4 h-4 text-surface-500 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{service.name}</p>
            <p className="text-surface-600 text-xs font-mono truncate">{service.full_name}</p>
          </div>
        </div>

        {/* Status badge */}
        <div className="flex-shrink-0">
          <StatusBadge status={status} size="sm" pulse={status === 'critical'} />
        </div>

        {/* Running / desired counts */}
        <div className="flex-shrink-0 text-right w-24">
          <p className="text-sm font-mono">
            <span className={cn(
              'font-semibold',
              service.running >= service.desired ? 'text-status-healthy' : 'text-status-critical',
            )}>
              {service.running}
            </span>
            <span className="text-surface-600 mx-1">/</span>
            <span className="text-surface-400">{service.desired}</span>
          </p>
          <p className="text-[10px] text-surface-600">running / desired</p>
        </div>

        {/* Pending indicator */}
        {service.pending > 0 && (
          <div className="flex-shrink-0 bg-status-warning/10 border border-status-warning/20 text-status-warning text-xs font-mono px-2 py-0.5 rounded">
            {service.pending} pending
          </div>
        )}

        {/* Expand chevron */}
        <ChevronDown
          className={cn(
            'w-4 h-4 text-surface-500 transition-transform flex-shrink-0',
            expanded && 'rotate-180',
          )}
        />
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Deployments */}
          {service.deployments.length > 0 && (
            <div className="bg-white/[0.02] rounded-lg p-3">
              <h4 className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-2">Deployments</h4>
              <div className="space-y-2">
                {service.deployments.map((dep, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className={cn(
                      'px-1.5 py-0.5 rounded text-xs font-mono',
                      dep.status === 'PRIMARY' ? 'bg-primary-500/20 text-primary-300' : 'bg-surface-700 text-surface-400',
                    )}>
                      {dep.status}
                    </span>
                    <span className="font-mono text-surface-300">
                      {dep.running}/{dep.desired}
                    </span>
                    {dep.updated_at && (
                      <span className="text-surface-600 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(dep.updated_at)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent events */}
          {service.events.length > 0 && (
            <div className="bg-white/[0.02] rounded-lg p-3">
              <h4 className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-2">Recent Events</h4>
              <div className="space-y-1.5">
                {service.events.map((evt, i) => (
                  <div key={i} className="text-xs text-surface-400 leading-relaxed">
                    <span className="text-surface-600 font-mono mr-2">
                      {formatRelativeTime(evt.created_at)}
                    </span>
                    {evt.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meta info */}
          <div className="flex items-center gap-4 text-xs text-surface-600">
            {service.launch_type && <span>Launch: {service.launch_type}</span>}
            {service.created_at && <span>Created: {formatRelativeTime(service.created_at)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);

    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;

    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;

    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  } catch {
    return dateStr;
  }
}
